import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { useElapsedSeconds } from "@/lib/hooks";
import { eurFmt } from "@/lib/constants";
import { fmtClock } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  TrendingUp, Users, Target, AlertTriangle, FileDown, FileText, ShieldCheck,
  Trophy, XCircle, CalendarClock, PieChart as PieIcon, Activity, Radio,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, AreaChart, Area, ComposedChart, CartesianGrid, Legend,
} from "recharts";

// Paleta única dos gráficos (segue os tokens do tema).
const C = {
  primary: "hsl(269 41% 66%)",
  ink: "hsl(210 11% 25%)",
  green: "#4C9A8E",
  red: "#D64545",
  amber: "#E0A100",
  grid: "hsl(220 13% 88%)",
  axis: "hsl(215 10% 45%)",
};

const nf = new Intl.NumberFormat("pt-PT", { maximumFractionDigits: 1 });

function ChartTip({ active, payload, label, unit = "" }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-md">
      {label != null && <div className="mb-1 font-medium text-foreground">{label}</div>}
      {payload.map((row) => (
        <div key={row.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: row.color }} />
          <span className="text-muted-foreground">{row.name}:</span>
          <span className="font-medium text-foreground">{nf.format(row.value)}{unit}</span>
        </div>
      ))}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, tone = "primary" }) {
  const tones = {
    primary: "bg-primary/15 text-primary",
    green: "bg-emerald-500/10 text-emerald-500",
    red: "bg-red-500/10 text-red-500",
    amber: "bg-amber-500/10 text-amber-600",
  };
  return (
    <Card className="p-5 border-border">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-poppins font-bold text-2xl">{value}</div>
      <div className="text-xs text-muted-foreground font-poppins uppercase tracking-wide mt-0.5">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function download(format) {
  window.open(`${API}/admin/report?format=${format}`, "_blank");
}

const RANGES = [
  { key: 90, label: "90 dias" },
  { key: 180, label: "6 meses" },
  { key: 365, label: "12 meses" },
];

function fmtDay(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
}

function EvolutionTab() {
  const [range, setRange] = useState(90);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-trends", range],
    queryFn: async () => (await api.get("/admin/trends", { params: { range } })).data,
  });
  const series = (data?.series || []).map((s) => ({ ...s, label: fmtDay(s.date) }));
  const livePoints = series.filter((s) => !s.derived).length;

  if (isLoading) return <p className="text-sm text-muted-foreground">A carregar histórico...</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {livePoints < 5
            ? "O histórico começa a ser registado agora — um ponto por dia. Os pontos antigos são estimados a partir das datas de criação."
            : `${series.length} pontos no período.`}
        </p>
        <div className="flex gap-1 rounded-lg border border-border p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${range === r.key ? "bg-primary text-black" : "text-muted-foreground hover:text-foreground"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {series.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Ainda sem dados de tendência.</Card>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2">
          <Card className="p-5">
            <h3 className="font-poppins font-semibold mb-1">MRR & Clientes ativos</h3>
            <p className="text-xs text-muted-foreground mb-4">Faturação recorrente e nº de contas ao longo do tempo</p>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={series} margin={{ left: -8, right: 8 }}>
                <defs>
                  <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.primary} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={C.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} width={54} tickFormatter={(v) => `${Math.round(v / 100) / 10}k`} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} width={28} allowDecimals={false} />
                <Tooltip content={<ChartTip />} />
                <Area yAxisId="l" type="monotone" dataKey="mrr" name="MRR (€)" stroke={C.primary} fill="url(#mrrFill)" strokeWidth={2} />
                <Line yAxisId="r" type="monotone" dataKey="activeClients" name="Clientes ativos" stroke={C.ink} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-5">
            <h3 className="font-poppins font-semibold mb-1">Pipeline & Propostas</h3>
            <p className="text-xs text-muted-foreground mb-4">Valor em aberto e acumulado de ganhas / perdidas</p>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ left: -8, right: 8 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} width={54} tickFormatter={(v) => `${Math.round(v / 100) / 10}k`} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} width={28} allowDecimals={false} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="l" type="monotone" dataKey="pipelineValue" name="Pipeline (€)" stroke={C.primary} strokeWidth={2} dot={false} />
                <Line yAxisId="r" type="monotone" dataKey="proposalsWon" name="Ganhas (acum.)" stroke={C.green} strokeWidth={2} dot={false} />
                <Line yAxisId="r" type="monotone" dataKey="proposalsLost" name="Perdidas (acum.)" stroke={C.red} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-5 xl:col-span-2">
            <h3 className="font-poppins font-semibold mb-1">Horas da agência (últimos 7 dias, por snapshot)</h3>
            <p className="text-xs text-muted-foreground mb-4">Reunião vs trabalho faturável vs trabalho interno</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ left: -8, right: 8 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: C.axis }} tickLine={false} axisLine={false} width={40} />
                <Tooltip content={<ChartTip unit="h" />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="billableHours7" name="Faturável" stackId="h" stroke={C.green} fill={C.green} fillOpacity={0.55} strokeWidth={1.5} />
                <Area type="monotone" dataKey="internalHours7" name="Interno" stackId="h" stroke={C.amber} fill={C.amber} fillOpacity={0.5} strokeWidth={1.5} />
                <Area type="monotone" dataKey="meetingHours7" name="Reunião" stackId="h" stroke={C.primary} fill={C.primary} fillOpacity={0.45} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
    </div>
  );
}

function CapacityCard({ member }) {
  const worked = member.workedHours || 0;
  const util = member.utilization || 0;
  const bar = Math.min(100, util);
  const toneText = util > 100 ? "text-destructive" : util > 85 ? "text-amber-500" : util >= 55 ? "text-emerald-500" : "text-muted-foreground";
  const segments = [
    { key: "meetingHours", label: "Reunião", color: C.primary },
    { key: "billableHours", label: "Faturável", color: C.green },
    { key: "internalHours", label: "Interno", color: C.amber },
  ];
  const total = segments.reduce((sum, s) => sum + (member[s.key] || 0), 0) || 1;

  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold">{member.name}</span>
        <span className={`text-xs font-semibold ${toneText}`}>{util}%</span>
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        {nf.format(worked)}h reais · {nf.format(member.plannedHours || 0)}h previstas
      </div>
      <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-muted">
        {segments.map((s) => {
          const value = member[s.key] || 0;
          if (!value) return null;
          return <div key={s.key} title={`${s.label}: ${nf.format(value)}h`} style={{ width: `${(value / total) * bar}%`, background: s.color }} />;
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
        {member.billableRatio != null && <span>{member.billableRatio}% faturável</span>}
        <span className={member.daysLogged < 3 ? "text-amber-600" : ""}>{member.daysLogged}/5 dias registados</span>
        {member.planningAccuracy != null && <span>plano {member.planningAccuracy}%</span>}
      </div>
    </div>
  );
}

function LiveInteractionRow({ interaction }) {
  const elapsed = useElapsedSeconds(interaction.startedAt);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-3">
      <UserAvatar user={{ name: interaction.userName }} size="h-8 w-8" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{interaction.userName}</div>
        <div className="truncate text-[11px] text-muted-foreground">
          {interaction.clientName} {(interaction.taskTitle || interaction.description) && `· ${interaction.taskTitle || interaction.description}`}
        </div>
      </div>
      <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 font-mono text-xs tabular-nums text-primary">{fmtClock(elapsed)}</span>
    </div>
  );
}

function LiveInteractionsPanel() {
  const { data: live = [] } = useQuery({
    queryKey: ["interactions-live"],
    queryFn: async () => (await api.get("/interactions/live")).data,
    refetchInterval: 20000,
  });
  return (
    <Card className="p-5">
      <h3 className="font-poppins font-semibold mb-1 flex items-center gap-2">
        <Radio className="h-4 w-4 text-primary" /> Agora a trabalhar
      </h3>
      <p className="text-xs text-muted-foreground mb-4">Interações de cliente em curso, ao vivo</p>
      {live.length === 0 ? (
        <p className="text-sm text-muted-foreground">Ninguém tem uma interação em curso neste momento.</p>
      ) : (
        <div className="space-y-2">
          {live.map((i) => <LiveInteractionRow key={i.id} interaction={i} />)}
        </div>
      )}
    </Card>
  );
}

export default function ExecutiveHub() {
  const { data: m } = useQuery({ queryKey: ["admin-dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });
  const { data: logs = [] } = useQuery({ queryKey: ["audit-logs"], queryFn: async () => (await api.get("/admin/audit-logs")).data });

  const workloadData = useMemo(
    () => (m?.workload || [])
      .filter((w) => w.hours > 0 || w.tasks > 0)
      .map((w) => ({ name: w.name.split(" ")[0], Reunião: w.meetingHours, Faturável: w.billableHours, Interno: w.internalHours })),
    [m],
  );

  if (!m) return <p className="text-muted-foreground text-sm">A carregar métricas...</p>;

  const funnelData = [
    { name: "Ganhas", value: m.funnel.wonCount, color: C.green },
    { name: "Perdidas", value: m.funnel.lostCount, color: C.red },
    { name: "Em aberto", value: m.funnel.openCount ?? Math.max(0, m.funnel.sentCount - m.funnel.wonCount - m.funnel.lostCount), color: C.primary },
  ];
  const conc = m.revenueConcentration || { top1Pct: 0, top3Pct: 0 };
  const concentrationRisk = conc.top1Pct >= 30 || conc.top3Pct >= 60;
  const windowDays = m.workloadWindowDays || 30;

  return (
    <div>
      <PageHeader
        title="Hub Executivo"
        subtitle="Métricas financeiras, pipeline e operações. Acesso restrito a Administradores."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => download("csv")} data-testid="export-csv"><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
            <Button onClick={() => download("pdf")} className="bg-primary text-black hover:bg-primary/90" data-testid="export-pdf"><FileText className="h-4 w-4 mr-1.5" />Relatório PDF</Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPI icon={TrendingUp} label="MRR" value={eurFmt.format(m.mrr)} sub="Faturação recorrente mensal" />
        <KPI icon={Users} label="Clientes Ativos" value={m.activeClients} sub={`${m.totalClients} no total`} />
        <KPI icon={Target} label="Taxa de Conversão" value={`${m.funnel.conversion}%`} sub={`${m.funnel.wonCount} ganhos / ${m.funnel.lostCount} perdidos`} />
        <KPI icon={AlertTriangle} label="Alertas" value={m.alerts.length} sub="Entregáveis em atraso" tone={m.alerts.length ? "red" : "green"} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI icon={PieIcon} label="Concentração de receita" value={`${conc.top3Pct}%`} sub={`Top 3 clientes · maior conta ${conc.top1Pct}%`} tone={concentrationRisk ? "amber" : "primary"} />
        <KPI icon={CalendarClock} label="Renovações 90 dias" value={eurFmt.format(m.renewalValue?.d90 || 0)} sub={`${eurFmt.format(m.renewalValue?.d30 || 0)} nos próximos 30 dias`} tone="primary" />
        <KPI icon={Activity} label="Pipeline em aberto" value={eurFmt.format(m.funnel.pipelineValue || 0)} sub={`${m.funnel.openCount || 0} propostas por decidir`} tone="primary" />
        <KPI icon={TrendingUp} label="Horas faturáveis (7d)" value={`${nf.format(m.hoursLast7?.billable || 0)}h`} sub={`${nf.format(m.hoursLast7?.internal || 0)}h internas · ${nf.format(m.hoursLast7?.meeting || 0)}h reunião`} tone="green" />
      </div>

      <Tabs defaultValue="ops">
        <TabsList className="mb-5 flex-wrap">
          <TabsTrigger value="ops" data-testid="tab-ops">Operações</TabsTrigger>
          <TabsTrigger value="evolucao" data-testid="tab-evolution">Evolução</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="rentabilidade" data-testid="tab-profit">Rentabilidade</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="tab-audit">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="ops">
          <div className="mb-5"><LiveInteractionsPanel /></div>
          <div className="grid lg:grid-cols-2 gap-5">
            <Card className="p-5">
              <h3 className="font-poppins font-semibold mb-1">Distribuição de carga</h3>
              <p className="text-xs text-muted-foreground mb-4">Horas por pessoa nos últimos {windowDays} dias — reunião vs trabalho faturável vs interno</p>
              {workloadData.length === 0 ? <p className="text-sm text-muted-foreground">Sem horas registadas no período.</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={workloadData}>
                    <CartesianGrid stroke={C.grid} vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: C.axis }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: C.axis }} tickLine={false} axisLine={false} width={32} />
                    <Tooltip content={<ChartTip unit="h" />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Reunião" stackId="a" fill={C.primary} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Faturável" stackId="a" fill={C.green} />
                    <Bar dataKey="Interno" stackId="a" fill={C.amber} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
            <Card className="p-5">
              <h3 className="font-poppins font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-primary" />Alertas de saúde de projeto</h3>
              {m.alerts.length === 0 ? <p className="text-sm text-muted-foreground">Sem entregáveis em atraso. 🎉</p> : (
                <div className="space-y-2">
                  {m.alerts.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                      <XCircle className="h-4 w-4 text-destructive shrink-0" />
                      <span className="text-sm font-roboto flex-1">{a.title}</span>
                      <span className="text-xs text-destructive">Prazo: {a.dueDate}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
          <Card className="mt-5 p-5">
            <div className="mb-4">
              <h3 className="font-poppins font-semibold">Capacidade semanal da equipa</h3>
              <p className="text-xs text-muted-foreground">
                Utilização = trabalho <b>real</b> desta semana (reunião + tempo registado) sobre {m.weeklyCapacityHours || 40}h.
                &ldquo;Dias registados&rdquo; indica quão completo está o Registo Diário.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {(m.capacity || []).map((member) => <CapacityCard key={member.id} member={member} />)}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="evolucao">
          <EvolutionTab />
        </TabsContent>

        <TabsContent value="pipeline">
          <div className="grid lg:grid-cols-2 gap-5">
            <Card className="p-5">
              <h3 className="font-poppins font-semibold mb-4">Funil de propostas</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg bg-muted/50 p-3"><div className="text-xs text-muted-foreground">Enviadas (valor)</div><div className="font-poppins font-bold text-lg">{eurFmt.format(m.funnel.sentValue)}</div></div>
                <div className="rounded-lg bg-muted/50 p-3"><div className="text-xs text-muted-foreground">Ganhas (valor)</div><div className="font-poppins font-bold text-lg text-primary">{eurFmt.format(m.funnel.wonValue)}</div></div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={funnelData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {funnelData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-5">
              <h3 className="font-poppins font-semibold mb-1 flex items-center gap-2"><PieIcon className="h-4 w-4 text-primary" />Concentração de receita</h3>
              <p className="text-xs text-muted-foreground mb-4">Quanto do MRR depende de cada cliente</p>
              {(m.revenueByClient || []).length === 0 ? <p className="text-sm text-muted-foreground">Sem avenças ativas registadas.</p> : (
                <>
                  {concentrationRisk && (
                    <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Risco de concentração: top 3 clientes = {conc.top3Pct}% da faturação.
                    </div>
                  )}
                  <div className="space-y-2">
                    {(m.revenueByClient || []).slice(0, 8).map((c) => (
                      <div key={c.id}>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="truncate font-medium">{c.name}</span>
                          <span className="shrink-0 text-muted-foreground">{eurFmt.format(c.mrr)} · {c.pct}%</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(3, c.pct)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>

            <Card className="p-5">
              <h3 className="font-poppins font-semibold mb-1 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Renovações a aproximar-se</h3>
              <p className="text-xs text-muted-foreground mb-4">Avenças recorrentes com renovação nos próximos 90 dias</p>
              <div className="mb-4 grid grid-cols-3 gap-2 text-center">
                {[["30 dias", m.renewalValue?.d30], ["60 dias", m.renewalValue?.d60], ["90 dias", m.renewalValue?.d90]].map(([lbl, val]) => (
                  <div key={lbl} className="rounded-lg bg-muted/50 p-2.5">
                    <div className="font-poppins font-bold text-sm">{eurFmt.format(val || 0)}</div>
                    <div className="text-[10px] text-muted-foreground">{lbl}</div>
                  </div>
                ))}
              </div>
              {(m.renewals || []).length === 0 ? <p className="text-sm text-muted-foreground">Sem renovações agendadas no período.</p> : (
                <div className="space-y-2">
                  {(m.renewals || []).map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${r.daysUntil <= 30 ? "bg-destructive" : r.daysUntil <= 60 ? "bg-amber-500" : "bg-primary"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{r.clientName} <span className="text-muted-foreground">· {r.title}</span></div>
                        <div className="text-[11px] text-muted-foreground">{r.renewalDate} · daqui a {r.daysUntil} dias</div>
                      </div>
                      <span className="shrink-0 text-sm font-poppins font-semibold">{eurFmt.format(r.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5">
              <h3 className="font-poppins font-semibold mb-4 flex items-center gap-2"><XCircle className="h-4 w-4 text-destructive" />Negócios perdidos</h3>
              {m.funnel.lost.length === 0 ? <p className="text-sm text-muted-foreground">Sem negócios perdidos.</p> : (
                <div className="space-y-2">
                  {m.funnel.lost.map((l, i) => (
                    <div key={i} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between"><span className="font-poppins font-medium text-sm">{l.title}</span><span className="text-xs text-muted-foreground">{eurFmt.format(l.value || 0)}</span></div>
                      {l.reason && <p className="text-xs text-destructive mt-1">Motivo: {l.reason}</p>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="rentabilidade">
          <Card className="p-5">
            <h3 className="font-poppins font-semibold mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-primary" />Índice de rentabilidade por cliente</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-2 font-poppins">Cliente</th><th className="py-2 font-poppins">Avença</th><th className="py-2 font-poppins">Horas</th><th className="py-2 font-poppins">Tarefas</th><th className="py-2 font-poppins">€/hora</th>
                  </tr>
                </thead>
                <tbody>
                  {m.profitability.map((p) => (
                    <tr key={p.id} className="border-b border-border/60">
                      <td className="py-2.5 font-medium">{p.name}</td>
                      <td className="py-2.5">{eurFmt.format(p.retainer)}</td>
                      <td className="py-2.5">{p.hours}h</td>
                      <td className="py-2.5">{p.tasks}</td>
                      <td className="py-2.5">{p.valuePerHour != null ? <Badge className="rounded-full bg-primary/15 text-primary">{eurFmt.format(p.valuePerHour)}</Badge> : <span className="text-muted-foreground">Sem dados</span>}</td>
                    </tr>
                  ))}
                  {m.profitability.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">Sem clientes.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
          <Card className="mt-5 p-5">
            <h3 className="font-poppins font-semibold mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-primary" />Rentabilidade por projeto</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground"><th className="py-2 font-poppins">Projeto</th><th className="py-2 font-poppins">Cliente</th><th className="py-2 font-poppins">Orçamento</th><th className="py-2 font-poppins">Horas registadas</th><th className="py-2 font-poppins">Valor por hora</th></tr></thead>
                <tbody>{(m.projectProfitability || []).map((project) => <tr key={project.id} className="border-b border-border/60"><td className="py-2.5 font-medium">{project.name}</td><td className="py-2.5 text-muted-foreground">{project.clientName}</td><td className="py-2.5">{eurFmt.format(project.budget)}</td><td className="py-2.5">{project.hours}h</td><td className="py-2.5">{project.valuePerHour != null ? <Badge className="rounded-full bg-primary/15 text-primary">{eurFmt.format(project.valuePerHour)}</Badge> : <span className="text-muted-foreground">Sem dados</span>}</td></tr>)}{!(m.projectProfitability || []).length && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">Sem projetos.</td></tr>}</tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="auditoria">
          <Card className="p-5">
            <h3 className="font-poppins font-semibold mb-4 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Log de auditoria</h3>
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
              {logs.length === 0 && <p className="text-sm text-muted-foreground">Sem registos.</p>}
              {logs.map((l) => (
                <div key={l.id} className="flex items-center gap-3 py-2 border-b border-border/50 text-sm" data-testid={`audit-${l.id}`}>
                  <Badge variant="secondary" className="rounded-full text-[10px] shrink-0">{l.action}</Badge>
                  <span className="text-muted-foreground text-xs shrink-0">{l.entity}</span>
                  <span className="font-roboto flex-1 truncate">{l.entityName} {l.details && <span className="text-muted-foreground">· {l.details}</span>}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{l.userName}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0">{new Date(l.timestamp).toLocaleString("pt-PT")}</span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
